import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  Paper,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import LockIcon from '@mui/icons-material/LockOutlined';
import LockOpenIcon from '@mui/icons-material/LockOpenOutlined';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import useProviderConfig from 'hooks/useProviderConfig';
import { getItemPriceOverrides, updateItem } from 'services/posMenuService';
import { priceTransform } from 'helpers/formPrice';

const PROVIDER_LABELS = { pos: 'POS', kiosk: 'Kiosk' };

const asBool = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(normalized)) return true;
    if (['false', '0', 'no', 'off'].includes(normalized)) return false;
    return fallback;
  }
  return Boolean(value);
};

const toRow = (overrides = []) => {
  const pickup = overrides.find((o) => o?.context?.deliveryType === 'pickup');
  const delivery = overrides.find((o) => o?.context?.deliveryType === 'delivery');
  const stored = pickup || delivery;
  return {
    pickupPrice: pickup?.price ?? '',
    price: delivery?.price ?? '',
    locked: stored ? asBool(stored.locked, true) : true,
  };
};

const PosProviderPriceOverridePopover = ({ itemId, availableInKiosk, onSaved = () => {} }) => {
  const { providers } = useProviderConfig();
  const [anchorEl, setAnchorEl] = useState(null);
  const [rows, setRows] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const providerKeys = useMemo(
    () => (asBool(availableInKiosk, false) ? ['pos', 'kiosk'] : ['pos']),
    [availableInKiosk]
  );

  const loadOverrides = useCallback(async () => {
    if (!itemId) return;
    setLoading(true);
    try {
      const results = await Promise.all(
        providerKeys.map((provider) => getItemPriceOverrides(itemId, provider))
      );
      const next = {};
      providerKeys.forEach((provider, index) => {
        next[provider] = toRow(Array.isArray(results[index]) ? results[index] : []);
      });
      setRows(next);
    } catch {
      toast.error('Failed to load price overrides');
    } finally {
      setLoading(false);
    }
  }, [itemId, providerKeys]);

  useEffect(() => {
    if (anchorEl) loadOverrides();
  }, [anchorEl, loadOverrides]);

  const handleChange = (provider, field, value) => {
    setRows((prev) => ({
      ...prev,
      [provider]: {
        ...prev[provider],
        [field]: /\d/.test(value) ? priceTransform(value) : '',
      },
    }));
  };

  const handleToggleLock = (provider) => {
    setRows((prev) => ({
      ...prev,
      [provider]: { ...prev[provider], locked: !prev[provider]?.locked },
    }));
  };

  const handleSave = async () => {
    const priceOverride = providerKeys.map((provider) => {
      const row = rows[provider] || { locked: true };
      if (row.locked) {
        return { context_type: 'PROVIDER', context_value: provider, locked: true };
      }
      return {
        context_type: 'PROVIDER',
        context_value: provider,
        locked: false,
        ...(row.pickupPrice !== '' && { pickupPrice: Number(row.pickupPrice) }),
        ...(row.price !== '' && { price: Number(row.price) }),
      };
    });

    setSaving(true);
    try {
      await updateItem(itemId, { provider: 'pos', priceOverride });
      toast.success('Price overrides saved');
      onSaved();
      setAnchorEl(null);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Failed to save price overrides');
    } finally {
      setSaving(false);
    }
  };

  if (!itemId) return null;

  return (
    <>
      <Tooltip title="Provider prices">
        <IconButton
          size="small"
          edge="end"
          onMouseDown={(e) => e.preventDefault()}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(e.currentTarget);
          }}
        >
          <SettingsOutlinedIcon sx={{ fontSize: '18px', color: '#929398' }} />
        </IconButton>
      </Tooltip>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <Paper>
          <List>
            <ListItem sx={{ borderBottom: (theme) => `1px solid ${theme.palette.divider}`, pb: 2, px: 3 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>Providers</Typography>
            </ListItem>
            {loading ? (
              <ListItem sx={{ px: 3, py: 2, justifyContent: 'center' }}>
                <CircularProgress size={20} />
              </ListItem>
            ) : (
              providerKeys.map((provider) => {
                const row = rows[provider] || { pickupPrice: '', price: '', locked: true };
                return (
                  <ListItem key={provider} sx={{ px: 3, py: 1.5 }}>
                    <Grid container maxWidth={514} spacing={2} alignItems="center">
                      <Grid item xs={4}>
                        <Box display="flex" alignItems="center">
                          {providers?.[provider]?.logoUrl && (
                            <img width="32px" src={providers[provider].logoUrl} alt={provider} />
                          )}
                          <Typography sx={{ fontSize: '12px', ml: '12px', color: '#3D4350' }}>
                            {providers?.[provider]?.label || PROVIDER_LABELS[provider]}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={3.5}>
                        <TextField
                          size="small"
                          label="Pickup price"
                          value={row.pickupPrice}
                          disabled={row.locked}
                          onChange={(e) => handleChange(provider, 'pickupPrice', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={3.5}>
                        <TextField
                          size="small"
                          label="Delivery price"
                          value={row.price}
                          disabled={row.locked}
                          onChange={(e) => handleChange(provider, 'price', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                        />
                      </Grid>
                      <Grid item xs={1} display="flex" alignItems="center">
                        <Tooltip title={row.locked ? 'Calculated from service fee' : 'Custom price'}>
                          <IconButton size="small" onClick={() => handleToggleLock(provider)}>
                            {row.locked ? (
                              <LockIcon sx={{ color: 'rgba(146, 147, 152, 1)' }} />
                            ) : (
                              <LockOpenIcon sx={{ color: '#FF8A00' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  </ListItem>
                );
              })
            )}
            <ListItem sx={{ borderTop: (theme) => `1px solid ${theme.palette.divider}`, px: 3, pt: 1.5 }}>
              <Button variant="contained" disabled={loading || saving} onClick={handleSave}>
                {saving ? <CircularProgress size={18} color="inherit" /> : <CheckIcon />}
              </Button>
              <Button sx={{ background: '#F2F2F2', color: 'black', ml: 1 }} onClick={() => setAnchorEl(null)}>
                <CloseIcon />
              </Button>
            </ListItem>
          </List>
        </Paper>
      </Popover>
    </>
  );
};

export default PosProviderPriceOverridePopover;
